import { useCallback, useState } from "react";
import { Baby, Loader2 } from "lucide-react";

import { useDeviceType, useWakeLock } from "@/hooks";
import { MonitorPage } from "@/components/monitor/MonitorUI";
import { BabyModePage } from "./BabyModePage";
import { ParentModePage } from "./ParentModePage";
import { DeviceSelectionPage } from "./DeviceSelectionPage";
import styles from "./AppPage.module.scss";

/**
 * Main App Page
 * Routes to Baby or Parent mode based on saved device type
 * Shows device selection if no type is set
 */
export function AppPage() {
  const { deviceType, isLoading, setDeviceType, error } = useDeviceType();
  const [isSaving, setIsSaving] = useState(false);

  const handleSelect = useCallback(
    async (type: Parameters<typeof setDeviceType>[0]) => {
      setIsSaving(true);

      try {
        return await setDeviceType(type);
      } finally {
        setIsSaving(false);
      }
    },
    [setDeviceType],
  );

  // Keep screen awake while in /app route (essential for baby monitor)
  // This prevents the screen from dimming/locking during monitoring
  useWakeLock(!isLoading);

  // Loading state with premium design
  if (isLoading && !isSaving && !deviceType) {
    return (
      <MonitorPage className={styles.loadingPage}>
        <div className={styles.loadingContent} role="status">
          <div className={styles.loadingIcon}>
            <Baby size={32} strokeWidth={2} />
          </div>
          <div className={styles.loadingSpinner}>
            <Loader2 size={24} strokeWidth={2} className={styles.spinIcon} />
          </div>
          <p className={styles.loadingTitle}>Loading your device.</p>
        </div>
      </MonitorPage>
    );
  }

  // No device type set - show selection
  if (!deviceType) {
    return <DeviceSelectionPage onSelect={handleSelect} initialError={error} />;
  }

  // Render appropriate mode
  if (deviceType === "BABY") {
    return <BabyModePage />;
  }

  return <ParentModePage />;
}
